import React from 'react';
import { ResponsiveBar } from '@nivo/bar';
import {
  Card,
  CardContent,
  Typography,
  useTheme,
  Box,
  Tooltip,
} from '@mui/material';
import { DadoIcms } from './types';
import { formatYAxisValue } from '../../Components/DrillDown/formatters';
import { VerticalBarChartSkeleton } from '../../Components/Skeletons/ChartSkeletons';

type BarChartProps = {
  data: DadoIcms[];
  title: string;
  loading?: boolean;
};

const BarChart: React.FC<BarChartProps> = ({ data, title, loading = false }) => {
  const theme = useTheme();

  return (
    <Card sx={{ p: 2, height: 590 }}>
      <CardContent sx={{ p: 0 }}>
        {!loading && (
          <Typography
            variant="subtitle1"
            align="center"
            sx={{ color: theme.palette.text.secondary, mb: 2 }}
          >
            {title}
          </Typography>
        )}
        
        {loading ? (
          <VerticalBarChartSkeleton height={440} showTitle={false} />
        ) : (
        <Box height={500}>
          <ResponsiveBar
            data={data}
            keys={['CRÉDITOS TOTAIS DE ICMS', 'DÉBITOS TOTAIS DE ICMS']}
            indexBy="tipo"
            groupMode="grouped"
            padding={0.4}
            innerPadding={8}
            margin={{ top: 20, right: 10, bottom: 80, left: 80 }}
            valueScale={{ type: 'linear' }}
            indexScale={{ type: 'band', round: true }}
            enableLabel={true}
            labelSkipWidth={24}
            labelSkipHeight={16}
            labelTextColor="#ffffff"
            valueFormat={(value) =>
              new Intl.NumberFormat('pt-BR', {
                style: 'currency',
                currency: 'BRL',
              }).format(value as number)
            }
            colors={({ id }) =>
              id === 'CRÉDITOS TOTAIS DE ICMS'
                ? theme.palette.success.main
                : id === 'DÉBITOS TOTAIS DE ICMS'
                ? theme.palette.error.main
                : '#888'
            }
            borderColor={{ from: 'color', modifiers: [['darker', 1.4]] }}
            axisTop={null}
            axisRight={null}
            axisBottom={{
              tickSize: 5,
              tickPadding: 5,
              tickRotation: -30,
              legend: 'Tipo',
              legendPosition: 'middle',
              legendOffset: 40,
            }}
            axisLeft={{
              tickSize: 4,
              tickPadding: 6,
              legend: 'Valor (R$)',
              legendPosition: 'middle',
              legendOffset: -100,
              format: formatYAxisValue
            }}
            tooltip={({ id, value, color }) => (
              <Box
                sx={{
                  background: theme.palette.background.paper,

                  p: 1,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 500, color: theme.palette.text.primary }}
                >
                  {id}
                </Typography>
                <Typography variant="body2" sx={{ color }}>
                  {new Intl.NumberFormat('pt-BR', {
                    style: 'currency',
                    currency: 'BRL',
                  }).format(value as number)}
                </Typography>
              </Box>
            )}
            legends={[
              {
                dataFrom: 'keys',
                anchor: 'bottom',
                direction: 'row',
                translateY: 70,
                itemWidth: 140, // Largura adequada (símbolo + texto próximos)
                itemHeight: 22,
                itemsSpacing: 50, // Espaçamento equilibrado entre itens
                itemDirection: 'left-to-right',
                symbolSize: 12, // Símbolo padrão
                symbolShape: 'circle',
                effects: [
                  {
                    on: 'hover',
                    style: {
                      itemTextColor: theme.palette.text.primary,
                      itemBackground: theme.palette.action.hover,
                    },
                  },
                ],
              },
            ]}
            animate
            motionConfig="gentle"
          />
        </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default BarChart;
